import Big from 'big.js'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { DotcEnums } from 'src/common/contracts/enums/dotc'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFromPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import { safeDiv } from 'src/common/utils/big-utils'
import { calcDynamicOfferAmountOut } from 'src/common/utils/offers/offerPrices'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  limitOrderOptionsAtom,
  pricePerUnitAtom,
  priceSwappedAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
} from 'src/pages/TradePage/stores/trade-store'
import { calculatePricePerUnit, validatePricePerUnit } from './utils'

const useUpdateTradeAmounts = (limitTrade?: boolean) => {
  const { priceFeeds } = useAllPriceFeeds()

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const [sellTokenAmount, setSellTokenAmount] = useAtom(sellTokenAmountAtom)
  const [buyTokenAmount, setBuyTokenAmount] = useAtom(buyTokenAmountAtom)
  const [priceSwapped, setPriceSwapped] = useAtom(priceSwappedAtom)
  const [limitOfferOptions, setLimitOfferOptions] = useAtom(
    limitOrderOptionsAtom
  )
  const setPricePerUnit = useSetAtom(pricePerUnitAtom)

  const getTokenPrice = (tokenAddress?: string): number | undefined =>
    tokenAddress ? getPriceFromPriceFeed(priceFeeds, tokenAddress) : undefined

  const calculateDynamicBuyAmount = (
    sellAmount: Big,
    percentage: number | null,
    percentageType: DotcEnums.PercentageType | null
  ): Big => {
    const sellTokenPrice = getTokenPrice(sellToken?.contractAddress)
    const buyTokenPrice = getTokenPrice(buyToken?.contractAddress)

    return sellTokenPrice && buyTokenPrice && limitTrade
      ? calcDynamicOfferAmountOut(
          sellAmount,
          percentage || null,
          percentageType,
          sellTokenPrice,
          buyTokenPrice
        )
      : buyTokenAmount
  }

  const updateSellAmount = (value: Big) => {
    setSellTokenAmount(value)
    const _buyAmount = calculateDynamicBuyAmount(
      value,
      limitOfferOptions.percentage,
      limitOfferOptions.percentageType
    )
    setBuyTokenAmount(_buyAmount)
    const _pricePerUnit = calculatePricePerUnit(value, _buyAmount, priceSwapped)
    setPricePerUnit(_pricePerUnit)
  }

  const updateBuyAmount = (value: Big) => {
    setBuyTokenAmount(value)
    const _pricePerUnit = calculatePricePerUnit(
      sellTokenAmount,
      value,
      priceSwapped
    )
    setPricePerUnit(_pricePerUnit)
  }

  const updatePricePerUnit = (value: Big) => {
    const sellTokenPrice = getPriceFromPriceFeed(
      priceFeeds,
      sellToken?.contractAddress
    )
    const buyTokenPrice = getPriceFromPriceFeed(
      priceFeeds,
      buyToken?.contractAddress
    )

    const { newPricePerUnit, newPercentage, newPercentageType } =
      validatePricePerUnit(
        value,
        sellTokenPrice,
        buyTokenPrice,
        limitOfferOptions.dynamic
      )

    const _buyAmount = priceSwapped
      ? safeDiv(sellTokenAmount, newPricePerUnit)
      : sellTokenAmount.mul(newPricePerUnit)
    setPricePerUnit(newPricePerUnit)
    setLimitOfferOptions(prev => ({
      ...prev,
      percentage: newPercentage,
      percentageType: newPercentageType,
    }))
    setBuyTokenAmount(_buyAmount)
  }

  const swapPricePerUnit = () => {
    setPriceSwapped(prevSwapped => {
      const newSwapped = !prevSwapped
      const newPricePerUnit = calculatePricePerUnit(
        sellTokenAmount,
        buyTokenAmount,
        newSwapped
      )
      setPricePerUnit(newPricePerUnit)
      return newSwapped
    })
  }

  const updatePercentage = (percentage: number | null) => {
    let percentageType = limitOfferOptions.percentageType
    if (percentage && limitOfferOptions.percentageType === null) {
      percentageType = DotcEnums.PercentageType.Plus
    } else if (!percentage && limitOfferOptions.percentageType) {
      percentageType = null
    }
    setLimitOfferOptions(prev => ({ ...prev, percentage, percentageType }))
    const _buyAmount = calculateDynamicBuyAmount(
      sellTokenAmount,
      percentage,
      percentageType
    )
    const _pricePerUnit = calculatePricePerUnit(
      sellTokenAmount,
      _buyAmount,
      priceSwapped
    )
    setBuyTokenAmount(_buyAmount)
    setPricePerUnit(_pricePerUnit)
  }

  const updatePercentageType = (
    percentageType: DotcEnums.PercentageType | null
  ) => {
    setLimitOfferOptions(prev => ({
      ...prev,
      percentageType,
    }))
    const _buyAmount = calculateDynamicBuyAmount(
      sellTokenAmount,
      limitOfferOptions.percentage,
      percentageType
    )
    const _pricePerUnit = calculatePricePerUnit(
      sellTokenAmount,
      _buyAmount,
      priceSwapped
    )
    setBuyTokenAmount(_buyAmount)
    setPricePerUnit(_pricePerUnit)
  }

  return {
    calculatePricePerUnit,
    updateSellAmount,
    updateBuyAmount,
    updatePricePerUnit,
    swapPricePerUnit,
    updatePercentage,
    updatePercentageType,
    priceSwapped,
  }
}

export default useUpdateTradeAmounts
